import _					from "lodash";
import { Auth } 			from "./Auth";

import { Server } 			from "./Server";

import SetupSettings 		from "./../settings/settings.json";

// Метки ключевых фраз для модели TensorFlow.js Speech Commands. Метки не должны совпадать со словами базовой модели ("stop", "go", "yes" и т.д.),
// _background_noise_ - служебная метка библиотеки для тишины/фонового шума
export const VOICE_LABELS = {
	start: 'start_phrase',
	stop: 'stop_phrase',
	noise: '_background_noise_'
};

// Максимальное количество записей каждой фразы для одного обучения
const MAX_EXAMPLES_PER_LABEL = 30;
// Максимальное количество сохранённых обученных моделей
const MAX_VOICE_MODELS = 10;
// Тишина (мс) после которой запись в режиме прослушивания отправляется на сервер
const SILENCE_TIMEOUT = 5000;
// Порог громкости (RMS сигнала микрофона) ниже которого звук считается тишиной
const SILENCE_RMS_THRESHOLD = 0.015;
// Период проверки громкости микрофона (мс)
const SILENCE_CHECK_INTERVAL = 100;
// После обнаружения старт-фразы стоп-фраза не принимается в течение этого времени (мс), чтобы хвост старт-фразы не остановил запись
const MIN_WAKE_RECORDING_DURATION = 1000;
// Защита от бесконечной записи в режиме прослушивания (мс)
const MAX_WAKE_RECORDING_DURATION = 120000;
// Параметры обучения и распознавания микро-модели
const TRAIN_EPOCHS = 50;
const PROBABILITY_THRESHOLD = 0.8;
const LISTEN_OVERLAP_FACTOR = 0.5;
const LISTEN_SUPPRESSION_TIME = 800;
// Предпочтительные форматы записи голоса (первый поддерживаемый браузером)
const RECORDER_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

// Библиотека сервиса AIAgent: голосовой ввод сообщений в чат
//
// 1 подход (push-to-talk): пользователь зажимает кнопку микрофона, запись идёт пока кнопка зажата, по отпусканию аудио отправляется по HTTP на сервер
// 2 подход (режим прослушивания): микрофон слушается постоянно с помощью TensorFlow.js Speech Commands; обученная на фразах пользователя
// микро-модель ждёт старт-фразу, после неё начинается запись, которая завершается стоп-фразой либо 5 секундами тишины и отправляется на сервер
export const AIAgentVoiceInput = {

	Name: "AIAgent Voice Input",

	Events: {

		// При разрыве соединения состояния распознавания неизвестны - сбрасываем
		"Socket Connection Closed": function() {

			this.VoiceTranscribing = {};

			Broadcast.call("AI Chat Voice State Changed");

			// Ответы на запросы содержимого моделей уже не придут - завершаем ожидание ошибкой (загруженное содержимое остаётся в кэше)
			_.each(this.voiceModelLoads, load => load.reject(new Error('Connection to the server was lost')));

			this.voiceModelLoads = {};

		}

	},

	Actions: {

		// Сервер сообщает состояние распознавания голосового сообщения в чате: 'transcribing' - аудио отправлено на распознавание, 'transcribed' - распознано
		"AIAgentVoiceState": function(state) {

			if (state.state === 'transcribing') this.VoiceTranscribing[state.chatId] = true;
			else delete this.VoiceTranscribing[state.chatId];

			Broadcast.call("AI Chat Voice State Changed", [state.chatId, state.state === 'transcribing']);

		},

		// Модель голосовой активации вместе с содержимым: выбранная модель при открытии дашборда (null - моделей нет) либо ответ на LoadUserVoice
		"UserVoice": function(voice) {

			if (!voice) {

				this.VoiceModels = [];
				this.selectedVoiceModelId = null;

			} else {

				this.VoiceModelsContent[voice.id] = Object.freeze(voice.model);

				let model = _.omit(voice, ['model']),
					index = _.findIndex(this.VoiceModels, m => m.id === model.id);

				if (index >= 0) this.VoiceModels.splice(index, 1, model);
				else this.VoiceModels = _.sortBy([...this.VoiceModels, model], 'id');

				if (model.selected) this.setSelectedVoiceModel(model.id, false);

				if (this.voiceModelLoads[voice.id]) {

					this.voiceModelLoads[voice.id].resolve(this.VoiceModelsContent[voice.id]);

					delete this.voiceModelLoads[voice.id];

				}

			}

			this.onVoiceModelsChanged();

		},

		// Список всех моделей пользователя без содержимого (при открытии окна обучения и после сохранения, выбора или удаления модели)
		"UserVoices": function(voices) {

			let voiceIds = _.map(voices, 'id');

			// Содержимое удалённых моделей больше не нужно. Содержимое остальных моделей остаётся в кэше - переключение на них моментальное
			this.VoiceModelsContent = _.pickBy(this.VoiceModelsContent, (content, voiceId) => _.includes(voiceIds, +voiceId) || this.pendingVoiceModels[voiceId]);

			// Модели, которые ещё сохраняются на сервере, оставляем в конце списка
			this.VoiceModels = [...voices, ..._.values(this.pendingVoiceModels)];

			let selectedModel = _.find(voices, voice => voice.selected);

			this.setSelectedVoiceModel(selectedModel ? selectedModel.id : null, false);

			this.onVoiceModelsChanged();

		},

		// Сервер сохранил новую модель: переносим содержимое с временного клиентского id на id записи в БД (список моделей придёт следом)
		"UserVoiceCreated": function(data) {

			if (!this.pendingVoiceModels[data.clientId]) return;

			delete this.pendingVoiceModels[data.clientId];

			if (this.VoiceModelsContent[data.clientId]) {

				this.VoiceModelsContent[data.id] = this.VoiceModelsContent[data.clientId];

				delete this.VoiceModelsContent[data.clientId];

			}

			// Распознаватель уже создан для этой модели - просто меняем id, создавать его заново не нужно
			if (this.transferRecognizerModelId === data.clientId) this.transferRecognizerModelId = data.id;

			if (this.selectedVoiceModelId === data.clientId) this.selectedVoiceModelId = data.id;

		}

	},

	init() {

		// id чатов, в которых сейчас идёт распознавание голосового сообщения (по данным сервера)
		this.VoiceTranscribing = {};

		// Мини-модели голосовой активации пользователя (таблица user_voices): описания моделей без содержимого (id, date, selected, wordLabels, epochs, examplesCount)
		this.VoiceModels = [];
		// Содержимое моделей по id (modelTopology, weightSpecs, weightData) - загружается с сервера только при переключении на модель и дальше хранится в кэше
		this.VoiceModelsContent = {};
		this.selectedVoiceModelId = null;
		// Ожидающие ответа запросы содержимого моделей по id: {promise, resolve, reject}
		this.voiceModelLoads = {};
		// Обученные модели, которые ещё сохраняются на сервере, по временному клиентскому id
		this.pendingVoiceModels = {};

		// Загруженные модули TensorFlow.js (подгружаются динамически при первом использовании, чтобы не утяжелять основной бандл)
		this.tf = null;
		this.speechCommands = null;
		this.tensorFlowPromise = null;

		// Базовая модель Speech Commands и распознаватель, обученный на фразах пользователя
		this.baseRecognizer = null;
		this.baseRecognizerPromise = null;
		this.transferRecognizer = null;
		this.transferRecognizerModelId = null;
		this.transferRecognizersCount = 0;

		// Текущая запись голоса (MediaRecorder)
		this.recording = null;

		// Режим постоянного прослушивания: 'off' | 'loading' | 'waiting' (ждём старт-фразу) | 'recording' | 'sending'
		this.listeningState = 'off';
		this.wakeWordProcess = null;

	},

	// Идёт ли сейчас распознавание голосового сообщения в чате
	isChatTranscribing(chatId) {

		return !!this.VoiceTranscribing[chatId];

	},

	// ---------------------------------------------------------------------------------------------------------------
	// Запись голоса и отправка на сервер
	// ---------------------------------------------------------------------------------------------------------------

	isVoiceInputSupported() {

		return !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

	},

	getRecorderMimeType() {

		return _.find(RECORDER_MIME_TYPES, mimeType => window.MediaRecorder.isTypeSupported(mimeType)) || '';

	},

	getAudioFileExtension(mimeType) {

		if (_.includes(mimeType, 'ogg')) return 'ogg';
		if (_.includes(mimeType, 'mp4')) return 'mp4';

		return 'webm';

	},

	isRecording() {

		return !!this.recording;

	},

	// Начинает запись с микрофона. Возвращает поток микрофона (нужен для определения тишины в режиме прослушивания)
	async startRecording() {

		if (this.recording) return this.recording.stream;

		if (!this.isVoiceInputSupported()) throw new Error('Voice recording is not supported by this browser');

		let stream = await navigator.mediaDevices.getUserMedia({audio: true});

		let mimeType = this.getRecorderMimeType(),
			recorder = new MediaRecorder(stream, mimeType ? {mimeType} : undefined);

		let recording = {
			stream,
			recorder,
			mimeType: recorder.mimeType || mimeType,
			chunks: [],
			startTime: Date.now()
		};

		recorder.ondataavailable = e => {

			if (e.data && e.data.size) recording.chunks.push(e.data);

		};

		recorder.start();

		this.recording = recording;

		Broadcast.call("AI Chat Voice Recording State Changed", [true]);

		return stream;

	},

	// Останавливает запись. Возвращает Blob с аудио (null - если записи не было)
	stopRecording() {

		let recording = this.recording;

		if (!recording) return Promise.resolve(null);

		this.recording = null;

		return new Promise(resolve => {

			recording.recorder.onstop = () => {

				_.each(recording.stream.getTracks(), track => track.stop());

				Broadcast.call("AI Chat Voice Recording State Changed", [false]);

				resolve(recording.chunks.length ? new Blob(recording.chunks, {type: recording.mimeType}) : null);

			};

			if (recording.recorder.state !== 'inactive') recording.recorder.stop();
			else recording.recorder.onstop();

		});

	},

	// Отменяет запись без отправки
	async cancelRecording() {

		await this.stopRecording();

	},

	// Отправляет записанное аудио по HTTP на сервер в чат. chatId может быть null - тогда сервер создаст новый чат в версии
	sendVoiceMessage(blob, versionId, chatId, model = null) {

		if (!blob || !blob.size || !versionId) return;

		let extension = this.getAudioFileExtension(blob.type);

		let formData = new FormData();

		formData.append("versionId", versionId);
		formData.append("chatId", chatId || 0);
		if (model) formData.append("model", model);
		formData.append("file", new File([blob], `voice-message.${extension}`, {type: blob.type}));

		let url = `${location.protocol}//${SetupSettings["domain"]}/ai/chat/voice`;

		let xhr = new XMLHttpRequest();

		xhr.open("POST", url);

		Auth.authorizeRequest(xhr);

		xhr.onerror = () => Broadcast.call("AI Chat Voice Message Send Failed", [versionId, chatId]);

		xhr.send(formData);

		Broadcast.call("AI Chat Voice Message Sent", [versionId, chatId]);

	},

	// ---------------------------------------------------------------------------------------------------------------
	// Мини-модели голосовой активации пользователя (хранятся на сервере в таблице user_voices, 1 запись = 1 модель).
	// При открытии дашборда сервер присылает только выбранную модель вместе с содержимым. Список остальных моделей (без содержимого)
	// запрашивается при открытии окна обучения, содержимое модели - только при переключении на неё, после чего хранится в кэше
	// ---------------------------------------------------------------------------------------------------------------

	getVoiceModels() {

		return this.VoiceModels;

	},

	getVoiceModel(modelId) {

		return _.find(this.VoiceModels, model => model.id === modelId) || null;

	},

	getSelectedVoiceModel() {

		return this.getVoiceModel(this.selectedVoiceModelId);

	},

	// Есть ли у пользователя обученная модель ключевых фраз
	hasVoiceModel() {

		return !!this.getSelectedVoiceModel();

	},

	// Загружено ли на клиент содержимое модели
	isVoiceModelLoaded(modelId) {

		return !!this.VoiceModelsContent[modelId];

	},

	// Выбирает модель локально (без ожидания сервера). notify - сообщить компонентам об изменении
	setSelectedVoiceModel(modelId, notify = true) {

		if (this.selectedVoiceModelId !== modelId) {

			// Режим прослушивания работает с распознавателем прежней модели - выключаем его
			if (this.wakeWordProcess) this.stopWakeWordMode();

			if (this.transferRecognizer && this.transferRecognizerModelId !== modelId) this.invalidateTransferRecognizer();

		}

		this.selectedVoiceModelId = modelId;

		_.each(this.VoiceModels, model => { model.selected = model.id === modelId; });

		if (notify) this.onVoiceModelsChanged();

	},

	onVoiceModelsChanged() {

		Broadcast.call("AI Chat Voice Models Changed");

	},

	// Содержимое модели: из кэша либо запросом на сервер (ответ придёт экшеном UserVoice). Повторные запросы одной модели не дублируются
	loadVoiceModelContent(modelId) {

		if (this.VoiceModelsContent[modelId]) return Promise.resolve(this.VoiceModelsContent[modelId]);

		if (!this.voiceModelLoads[modelId]) {

			let load = {};

			load.promise = new Promise((resolve, reject) => {

				load.resolve = resolve;
				load.reject = reject;

			});

			this.voiceModelLoads[modelId] = load;

			Server.sendToServer({
				LoadUserVoice: {
					voiceId: modelId
				}
			});

		}

		return this.voiceModelLoads[modelId].promise;

	},

	// Пустой набор записей фраз для обучения (записи хранятся только на клиенте до начала обучения)
	createEmptyExamples() {

		return _.mapValues(_.invert(VOICE_LABELS), () => []);

	},

	// Сохраняет новую обученную модель на сервере. Модель занимает сотни килобайт - сокет сбрасывает соединение на таких сообщениях,
	// поэтому она отправляется по HTTP JSON-файлом. До ответа сервера модель живёт под временным id и сразу становится выбранной,
	// сервер сообщит id записи в БД экшеном UserVoiceCreated и пришлёт обновлённый список моделей
	saveVoiceModel(model) {

		let content = Object.freeze(_.pick(model, ['modelTopology', 'weightSpecs', 'weightData'])),
			voiceModel = _.pick(model, ['id', 'date', 'wordLabels', 'epochs', 'examplesCount']);

		this.VoiceModelsContent[voiceModel.id] = content;
		this.pendingVoiceModels[voiceModel.id] = voiceModel;

		this.VoiceModels = [...this.VoiceModels, voiceModel];

		this.setSelectedVoiceModel(voiceModel.id);

		let voiceData = {
			options: _.pick(voiceModel, ['wordLabels', 'epochs', 'examplesCount']),
			model: content
		};

		let formData = new FormData();

		formData.append("clientId", voiceModel.id);
		formData.append("file", new File([JSON.stringify(voiceData)], `voice-model.json`, {type: 'application/json'}));

		let xhr = new XMLHttpRequest();

		xhr.open("POST", `${location.protocol}//${SetupSettings["domain"]}/user/voice`);

		Auth.authorizeRequest(xhr);

		xhr.onerror = () => {

			// Модель не сохранена - убираем её и запрашиваем актуальный список моделей
			delete this.pendingVoiceModels[voiceModel.id];
			delete this.VoiceModelsContent[voiceModel.id];

			this.VoiceModels = _.filter(this.VoiceModels, m => m.id !== voiceModel.id);

			Server.sendToServer({
				LoadUserVoices: {}
			});

			Broadcast.call("AI Chat Voice Model Save Failed");

		};

		xhr.send(formData);

	},

	getMaxExamplesPerLabel() {

		return MAX_EXAMPLES_PER_LABEL;

	},

	getMaxVoiceModels() {

		return MAX_VOICE_MODELS;

	},

	// Записи фраз, участвующие в обучении (включённые пользователем), сгруппированные по меткам
	getTrainingExamples(examplesByLabel) {

		return _.mapValues(examplesByLabel, examples => _.filter(examples, example => example.enabled !== false));

	},

	arrayBufferToBase64(buffer) {

		let bytes = new Uint8Array(buffer),
			binary = '';

		for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));

		return window.btoa(binary);

	},

	base64ToArrayBuffer(base64) {

		let binary = window.atob(base64),
			bytes = new Uint8Array(binary.length);

		for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

		return bytes.buffer;

	},

	// ---------------------------------------------------------------------------------------------------------------
	// TensorFlow.js Speech Commands: запись примеров, обучение и загрузка микро-модели
	// ---------------------------------------------------------------------------------------------------------------

	// Подгружаем TensorFlow.js и Speech Commands динамически при первом использовании
	loadTensorFlow() {

		if (!this.tensorFlowPromise) {

			this.tensorFlowPromise = Promise.all([
				import(/* webpackChunkName: "tensorflow" */ '@tensorflow/tfjs'),
				import(/* webpackChunkName: "tensorflow" */ '@tensorflow-models/speech-commands')
			]).then(([tf, speechCommands]) => {

				this.tf = tf;
				this.speechCommands = speechCommands;

			}).catch(e => {

				this.tensorFlowPromise = null;

				throw e;

			});

		}

		return this.tensorFlowPromise;

	},

	// Базовая модель Speech Commands (загружается один раз)
	async getBaseRecognizer() {

		if (!this.baseRecognizerPromise) {

			this.baseRecognizerPromise = this.loadTensorFlow().then(async () => {

				let recognizer = this.speechCommands.create('BROWSER_FFT');

				await recognizer.ensureModelLoaded();

				this.baseRecognizer = recognizer;

				return recognizer;

			}).catch(e => {

				this.baseRecognizerPromise = null;

				throw e;

			});

		}

		return this.baseRecognizerPromise;

	},

	// Новый распознаватель для дообучения на фразах пользователя (имя должно быть уникальным в рамках базовой модели)
	async createTransferRecognizer() {

		let baseRecognizer = await this.getBaseRecognizer();

		return baseRecognizer.createTransfer(`ai-chat-voice-${++this.transferRecognizersCount}`);

	},

	// Записывает один пример фразы с микрофона (около 1 секунды). Спектрограмма хранится только в памяти до обучения модели
	async collectExample(transferRecognizer, label) {

		let spectrogram = await transferRecognizer.collectExample(label);

		return {
			id: `${Date.now()}-${Math.round(Math.random() * 1e6)}`,
			date: new Date().toISOString(),
			enabled: true,
			// Замораживаем, чтобы Vue не делал большие данные спектрограммы реактивными
			spectrogram: Object.freeze({data: spectrogram.data, frameSize: spectrogram.frameSize})
		};

	},

	// Загружает записи фраз в распознаватель (через Dataset библиотеки)
	loadExamplesIntoRecognizer(transferRecognizer, examplesByLabel) {

		let dataset = new this.speechCommands.Dataset();

		_.each(examplesByLabel, (examples, label) => {

			_.each(examples, example => {

				dataset.addExample({
					label,
					spectrogram: example.spectrogram
				});

			});

		});

		if (dataset.empty()) throw new Error('There are no recorded phrases to train the model');

		// Распознаватель только что создан и пуст (clearExisting = true бросает ошибку на пустом наборе данных)
		transferRecognizer.loadExamples(dataset.serialize(), false);

	},

	// Обучает микро-модель на включённых записях фраз (examples - записи по меткам). onProgress({epoch, epochs, loss, accuracy}) - прогресс обучения
	// Возвращает {model, transferRecognizer} - сериализованную модель для сохранения на сервере (saveVoiceModel) и готовый распознаватель
	async trainVoiceModel(examples, onProgress) {

		let examplesByLabel = this.getTrainingExamples(examples);

		let labelsWithExamples = _.filter(_.keys(examplesByLabel), label => examplesByLabel[label].length);

		if (labelsWithExamples.length < 2) throw new Error('Record at least two different phrases to train the model');

		let transferRecognizer = await this.createTransferRecognizer();

		this.loadExamplesIntoRecognizer(transferRecognizer, examplesByLabel);

		await transferRecognizer.train({
			epochs: TRAIN_EPOCHS,
			callback: {
				onEpochEnd: async (epoch, logs) => {

					if (onProgress) onProgress({epoch: epoch + 1, epochs: TRAIN_EPOCHS, loss: logs.loss, accuracy: logs.acc});

				}
			}
		});

		let model = await this.serializeModel(transferRecognizer);

		return {model, transferRecognizer};

	},

	// Сериализует обученную модель (описание + топология и веса). id - временный клиентский id до сохранения модели в БД
	async serializeModel(transferRecognizer) {

		let artifacts = null;

		await transferRecognizer.save(this.tf.io.withSaveHandler(async modelArtifacts => {

			artifacts = modelArtifacts;

			return {modelArtifactsInfo: {dateSaved: new Date(), modelTopologyType: 'JSON'}};

		}));

		// В новых версиях TensorFlow.js веса могут приходить массивом буферов - склеиваем в один
		let weightData = artifacts.weightData;

		if (_.isArray(weightData)) weightData = this.tf.io.CompositeArrayBuffer.join(weightData);

		return {
			id: `${Date.now()}-${Math.round(Math.random() * 1e6)}`,
			date: new Date().toISOString(),
			wordLabels: transferRecognizer.wordLabels(),
			modelTopology: artifacts.modelTopology,
			weightSpecs: artifacts.weightSpecs,
			weightData: this.arrayBufferToBase64(weightData),
			epochs: TRAIN_EPOCHS,
			// Количество записей по каждой фразе, на которых обучена модель
			examplesCount: transferRecognizer.countExamples()
		};

	},

	// Создаёт распознаватель из сохранённой модели (по умолчанию - выбранной модели пользователя). Кэшируется до смены модели.
	// Если содержимого модели ещё нет на клиенте - дожидаемся его загрузки с сервера
	async getVoiceModelRecognizer(model = this.getSelectedVoiceModel()) {

		if (!model) throw new Error('Voice model is not trained yet');

		if (this.transferRecognizer && this.transferRecognizerModelId === model.id) return this.transferRecognizer;

		let content = await this.loadVoiceModelContent(model.id);

		// Пока ждали содержимое, распознаватель этой модели мог уже быть создан
		if (this.transferRecognizer && this.transferRecognizerModelId === model.id) return this.transferRecognizer;

		this.invalidateTransferRecognizer();

		let transferRecognizer = await this.createTransferRecognizer();

		// При загрузке через свой обработчик (не из localStorage библиотеки) список слов не восстанавливается - выставляем его сами
		transferRecognizer.words = model.wordLabels;

		await transferRecognizer.load(this.tf.io.fromMemory({
			modelTopology: content.modelTopology,
			weightSpecs: content.weightSpecs,
			weightData: this.base64ToArrayBuffer(content.weightData)
		}));

		this.transferRecognizer = transferRecognizer;
		this.transferRecognizerModelId = model.id;

		return transferRecognizer;

	},

	invalidateTransferRecognizer() {

		if (this.transferRecognizer?.isListening()) this.transferRecognizer.stopListening();

		this.transferRecognizer = null;
		this.transferRecognizerModelId = null;

	},

	// Запускает распознавание ключевых фраз с микрофона. callback({label, score, scores}) вызывается для каждого распознанного окна
	async startWordListening(transferRecognizer, callback, options = {}) {

		if (transferRecognizer.isListening()) await transferRecognizer.stopListening();

		let wordLabels = transferRecognizer.wordLabels();

		await transferRecognizer.listen(async result => {

			let scores = _.zipObject(wordLabels, Array.from(result.scores)),
				label = _.maxBy(wordLabels, wordLabel => scores[wordLabel]);

			callback({label, score: scores[label], scores});

		}, {
			probabilityThreshold: _.isFinite(options.probabilityThreshold) ? options.probabilityThreshold : PROBABILITY_THRESHOLD,
			overlapFactor: LISTEN_OVERLAP_FACTOR,
			suppressionTimeMillis: LISTEN_SUPPRESSION_TIME,
			invokeCallbackOnNoiseAndUnknown: true
		});

	},

	async stopWordListening(transferRecognizer) {

		if (transferRecognizer?.isListening()) await transferRecognizer.stopListening();

	},

	// ---------------------------------------------------------------------------------------------------------------
	// Режим постоянного прослушивания микрофона (2 подход)
	// ---------------------------------------------------------------------------------------------------------------

	isWakeWordModeEnabled() {

		return this.listeningState !== 'off';

	},

	getListeningState() {

		return this.listeningState;

	},

	setListeningState(state) {

		this.listeningState = state;

		Broadcast.call("AI Chat Voice Listening State Changed", [state]);

	},

	// Включает режим прослушивания. getTarget() возвращает {versionId, chatId} в момент отправки записи (пользователь мог переключить чат)
	async startWakeWordMode(getTarget) {

		if (this.wakeWordProcess) return;

		let process = {getTarget, recordingStartTime: 0, silenceWatch: null};

		this.wakeWordProcess = process;

		this.setListeningState('loading');

		try {

			let transferRecognizer = await this.getVoiceModelRecognizer();

			// Режим могли выключить пока загружалась модель
			if (this.wakeWordProcess !== process) return;

			process.transferRecognizer = transferRecognizer;

			await this.startWordListening(transferRecognizer, result => this.processWakeWord(process, result));

			if (this.wakeWordProcess !== process) return this.stopWordListening(transferRecognizer);

			this.setListeningState('waiting');

		} catch (e) {

			if (this.wakeWordProcess === process) await this.stopWakeWordMode();

			throw e;

		}

	},

	async stopWakeWordMode() {

		let process = this.wakeWordProcess;

		if (!process) return;

		this.wakeWordProcess = null;

		this.stopSilenceWatch(process);

		await this.stopWordListening(process.transferRecognizer);

		// Незавершённую запись отбрасываем
		if (this.recording) await this.cancelRecording();

		this.setListeningState('off');

	},

	// Реакция на распознанную фразу в режиме прослушивания
	async processWakeWord(process, result) {

		if (this.wakeWordProcess !== process) return;

		Broadcast.call("AI Chat Voice Word Detected", [result]);

		if (this.listeningState === 'waiting' && result.label === VOICE_LABELS.start) {

			await this.startWakeRecording(process);

		}
		else if (this.listeningState === 'recording' && result.label === VOICE_LABELS.stop && Date.now() - process.recordingStartTime > MIN_WAKE_RECORDING_DURATION) {

			await this.finishWakeRecording(process);

		}

	},

	async startWakeRecording(process) {

		this.setListeningState('recording');

		process.recordingStartTime = Date.now();

		try {

			let stream = await this.startRecording();

			if (this.wakeWordProcess !== process || this.listeningState !== 'recording') return;

			// Запись завершается по тишине в 5 секунд (стоп-фраза обрабатывается в processWakeWord)
			this.startSilenceWatch(process, stream, () => this.finishWakeRecording(process));

		} catch (e) {

			if (this.wakeWordProcess === process) this.setListeningState('waiting');

			throw e;

		}

	},

	// Завершает запись в режиме прослушивания и отправляет её на сервер (если в записи была речь)
	async finishWakeRecording(process) {

		if (this.wakeWordProcess !== process || this.listeningState !== 'recording') return;

		this.setListeningState('sending');

		let voiceDetected = !!process.silenceWatch?.voiceDetected;

		this.stopSilenceWatch(process);

		let blob = await this.stopRecording();

		if (this.wakeWordProcess !== process) return;

		if (blob && voiceDetected) {

			let target = process.getTarget();

			if (target?.versionId) this.sendVoiceMessage(blob, target.versionId, target.chatId, target.model);

		}

		this.setListeningState('waiting');

	},

	// Следит за громкостью микрофона: если тишина длится дольше SILENCE_TIMEOUT - вызывает onSilence
	startSilenceWatch(process, stream, onSilence) {

		this.stopSilenceWatch(process);

		let AudioContextClass = window.AudioContext || window.webkitAudioContext;

		if (!AudioContextClass) return;

		let audioContext = new AudioContextClass(),
			source = audioContext.createMediaStreamSource(stream),
			analyser = audioContext.createAnalyser();

		analyser.fftSize = 2048;

		source.connect(analyser);

		let samples = new Float32Array(analyser.fftSize);

		let silenceWatch = {
			audioContext,
			voiceDetected: false,
			lastVoiceTime: Date.now(),
			startTime: Date.now(),
			interval: null
		};

		silenceWatch.interval = setInterval(() => {

			analyser.getFloatTimeDomainData(samples);

			let sum = 0;

			for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];

			let rms = Math.sqrt(sum / samples.length),
				now = Date.now();

			if (rms > SILENCE_RMS_THRESHOLD) {

				silenceWatch.voiceDetected = true;
				silenceWatch.lastVoiceTime = now;

			}

			Broadcast.call("AI Chat Voice Level Changed", [rms]);

			if (now - silenceWatch.lastVoiceTime > SILENCE_TIMEOUT || now - silenceWatch.startTime > MAX_WAKE_RECORDING_DURATION) onSilence();

		}, SILENCE_CHECK_INTERVAL);

		process.silenceWatch = silenceWatch;

	},

	stopSilenceWatch(process) {

		let silenceWatch = process.silenceWatch;

		if (!silenceWatch) return;

		process.silenceWatch = null;

		clearInterval(silenceWatch.interval);

		silenceWatch.audioContext.close().catch(() => {});

	}

};
